import { inject } from '@angular/core'
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot, UrlTree, CanActivateFn } from '@angular/router'
import { AuthGuardData, createAuthGuard } from 'keycloak-angular'
import Keycloak from 'keycloak-js'

// Add an interface for the route data with roles
interface RouteDataWithRoles {
    roles?: string[]
    [key: string]: any
}

/**
 * @description Function to check if the user has access to the route based on authentication and roles
 * Used in the individual component routes in the CanActivate property.
 */
export const isAccessAllowed = async (
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
    authData: AuthGuardData
): Promise<boolean | UrlTree> => {
    const { authenticated, grantedRoles } = authData

    // If not authenticated, redirect to keycloak login
    if (!authenticated) {
        const keycloak = inject(Keycloak)
        await keycloak.login({
            redirectUri: window.location.origin + state.url
        })
        return false
    }

    const requiredRoles: string[] | undefined = (route.data as RouteDataWithRoles)?.roles

    // If no roles required, allow access
    if (!requiredRoles || requiredRoles.length === 0) {
        return true
    }

    // More efficient role checking - early return if any role matches
    for (const role of requiredRoles) {
        if (grantedRoles.realmRoles.includes(role)) {
            return true
        }

        // Check resource roles
        for (const resourceRoles of Object.values(grantedRoles.resourceRoles)) {
            if (resourceRoles.includes(role)) {
                return true
            }
        }
    }

    // If user doesn't have required roles, redirect to error page
    const router = inject(Router)
    return router.parseUrl('/error')
}

/**
 * @description A guard for routes where user authentication needs to be checked.
 * This replaces the previous class-based KeycloakAuthGuard implementation.
 */
export const CanAuthenticationGuard: CanActivateFn = createAuthGuard<CanActivateFn>(isAccessAllowed)
